import { Button } from '@/components/ui/Button';
import {
  useDeleteNotification,
  useMarkAllAsRead,
  useNotifications,
  useNotificationsPagination,
  useUpdateReadStatus,
} from '@/hooks/useNotifications';
import { useNotificationsStore } from '@/store/notifications.store';
import type { Notification } from '@/types/notification.types';
import { cn } from '@/utils/cn';
import { format } from '@/utils/format';
import {
  AlertCircle,
  Bell,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

function getActionLabel(templateId: string) {
  switch (templateId) {
    case 'calculate-dsn':
      return 'DSN';
    case 'generate-bulletin':
      return 'Bulletins';
    case 'generate-edition':
      return 'Éditions';
    case 'vault-sync':
      return 'Coffres-forts';
    case 'bdese-update':
      return 'BDESE';
    default:
      return 'Automatisation';
  }
}

function SearchBar() {
  const { searchQuery, setSearchQuery } = useNotificationsStore();
  const [localSearch, setLocalSearch] = useState(searchQuery);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(localSearch), 400);
    return () => window.clearTimeout(timer);
  }, [localSearch, setSearchQuery]);

  return (
    <div className="relative min-w-0 flex-1">
      <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
      <input
        type="search"
        value={localSearch}
        onChange={event => setLocalSearch(event.target.value)}
        className="focus:border-primary-500 focus:ring-primary-100 h-10 w-full rounded-xl border border-neutral-300 pr-10 pl-9 text-sm placeholder:text-neutral-400/50 focus:ring-2"
        placeholder="Rechercher dans les notifications"
        aria-label="Rechercher dans les notifications"
      />
      {localSearch && (
        <button
          type="button"
          onClick={() => setLocalSearch('')}
          className="focus-visible:ring-primary-500 absolute top-1/2 right-2 -translate-y-1/2 rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 focus-visible:ring-2 focus-visible:outline-none"
          aria-label="Effacer la recherche"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function ViewTabs() {
  const { statusFilter, setStatusFilter } = useNotificationsStore();

  return (
    <div
      className="inline-flex rounded-xl bg-neutral-100 p-1"
      role="group"
      aria-label="Type de notifications"
    >
      <button
        type="button"
        onClick={() => setStatusFilter('failed')}
        aria-pressed={statusFilter === 'failed'}
        className={cn(
          'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
          statusFilter === 'failed'
            ? 'bg-white text-neutral-900 shadow-sm'
            : 'text-neutral-600 hover:text-neutral-900'
        )}
      >
        À traiter
      </button>
      <button
        type="button"
        onClick={() => setStatusFilter('all')}
        aria-pressed={statusFilter === 'all'}
        className={cn(
          'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
          statusFilter === 'all'
            ? 'bg-white text-neutral-900 shadow-sm'
            : 'text-neutral-600 hover:text-neutral-900'
        )}
      >
        Historique
      </button>
    </div>
  );
}

function NotificationCard({ notification }: { notification: Notification }) {
  const failure = notification.status === 'failed';
  const { mutate: updateReadStatus, isPending: isUpdating } =
    useUpdateReadStatus();
  const { mutate: hideNotification, isPending: isHiding } =
    useDeleteNotification();

  return (
    <article
      className={cn(
        'rounded-2xl border bg-white p-4 shadow-sm sm:p-5',
        failure && !notification.isRead
          ? 'border-error-200'
          : 'border-neutral-200'
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
            failure
              ? 'bg-error-50 text-error-600'
              : 'bg-success-50 text-success-600'
          )}
        >
          {failure ? (
            <AlertCircle className="h-5 w-5" />
          ) : (
            <Check className="h-5 w-5" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    failure
                      ? 'bg-error-50 text-error-700'
                      : 'bg-success-50 text-success-700'
                  )}
                >
                  {failure ? 'Action requise' : 'Terminé'}
                </span>
                <span className="text-xs font-medium text-neutral-500">
                  {getActionLabel(notification.templateId)}
                </span>
              </div>
              <h2 className="mt-2 text-sm font-semibold text-neutral-900 sm:text-base">
                {notification.subject}
              </h2>
            </div>
            <time
              dateTime={notification.createdAt}
              className="shrink-0 text-xs text-neutral-500"
            >
              {format.dateTime(notification.createdAt)}
            </time>
          </div>

          {failure && notification.errorMessage && (
            <p className="mt-2 line-clamp-2 text-sm leading-5 text-neutral-600">
              {notification.errorMessage}
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-3">
            {notification.folderUuid && (
              <Link
                to={`/folders/${notification.folderUuid}`}
                className="text-primary-700 hover:bg-primary-50 focus-visible:ring-primary-500 inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium focus-visible:ring-2 focus-visible:outline-none"
              >
                Voir le dossier
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            )}
            {failure && !notification.isRead && (
              <button
                type="button"
                onClick={() =>
                  updateReadStatus({
                    notificationId: notification.uuid,
                    data: { isRead: true },
                  })
                }
                disabled={isUpdating}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-50"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Marquer comme traité
              </button>
            )}
            <button
              type="button"
              onClick={() => hideNotification(notification.uuid)}
              disabled={isHiding}
              className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Masquer
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function Pagination() {
  const {
    currentPage,
    totalPages,
    totalCount,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
  } = useNotificationsPagination();

  if (totalPages <= 1) return null;

  return (
    <nav
      aria-label="Pagination des notifications"
      className="flex items-center justify-between gap-3"
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={prevPage}
        disabled={!hasPrev}
        className="gap-1"
      >
        <ChevronLeft className="h-4 w-4" />
        Précédent
      </Button>
      <span className="text-sm text-neutral-500">
        Page {currentPage} sur {totalPages} · {totalCount} résultat
        {totalCount > 1 ? 's' : ''}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={nextPage}
        disabled={!hasNext}
        className="gap-1"
      >
        Suivant
        <ChevronRight className="h-4 w-4" />
      </Button>
    </nav>
  );
}

function EmptyState() {
  const statusFilter = useNotificationsStore(state => state.statusFilter);
  const attentionView = statusFilter === 'failed';

  return (
    <div className="rounded-2xl border border-dashed border-neutral-300 bg-white px-6 py-12 text-center">
      {attentionView ? (
        <CheckCircle2 className="text-success-600 mx-auto h-8 w-8" />
      ) : (
        <Bell className="mx-auto h-8 w-8 text-neutral-400" />
      )}
      <h2 className="mt-3 font-semibold text-neutral-900">
        {attentionView ? 'Aucune action requise' : 'Aucune notification'}
      </h2>
      <p className="mt-1 text-sm text-neutral-500">
        {attentionView
          ? 'Tout est en ordre pour le moment.'
          : 'L’historique de vos automatisations apparaîtra ici.'}
      </p>
    </div>
  );
}

export const NotificationsPage = () => {
  const { notifications, isLoading, error, unreadCount, refetch } =
    useNotifications();
  const statusFilter = useNotificationsStore(state => state.statusFilter);
  const { mutate: markAllAsRead, isPending: isMarkingAllAsRead } =
    useMarkAllAsRead();

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="flex flex-col gap-4 border-b border-neutral-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Notifications</h1>
          <p className="mt-1 text-sm text-neutral-600">
            Les alertes utiles d’abord, l’historique quand vous en avez besoin.
          </p>
        </div>
        {statusFilter === 'failed' && unreadCount > 0 && (
          <Button
            type="button"
            variant="outline"
            onClick={() => markAllAsRead()}
            disabled={isMarkingAllAsRead}
            className="w-full gap-2 sm:w-auto"
          >
            {isMarkingAllAsRead ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            Tout marquer comme traité ({unreadCount})
          </Button>
        )}
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <ViewTabs />
        <SearchBar />
      </div>

      {isLoading ? (
        <div className="space-y-3" aria-label="Chargement des notifications">
          {[0, 1, 2].map(index => (
            <div
              key={index}
              className="h-32 animate-pulse rounded-2xl bg-neutral-100"
            />
          ))}
        </div>
      ) : error ? (
        <div className="border-error-200 bg-error-50 rounded-2xl border p-8 text-center">
          <AlertCircle className="text-error-600 mx-auto h-7 w-7" />
          <h2 className="text-error-900 mt-2 font-semibold">
            Les notifications n’ont pas pu être chargées.
          </h2>
          <Button
            type="button"
            variant="outline"
            onClick={() => refetch()}
            className="mt-4 gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Réessayer
          </Button>
        </div>
      ) : notifications.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <div className="space-y-3" aria-live="polite">
            {notifications.map(notification => (
              <NotificationCard
                key={notification.uuid}
                notification={notification}
              />
            ))}
          </div>
          <Pagination />
        </>
      )}
    </div>
  );
};
